import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from 'react-tooltip';
import Loader from '@app/component/loader/Loader';
import DataProvider from '@app/model/_common/DataProvider';
import DataPlatform from '@app/model/_common/DataPlatform';
import RatesType from '@app/model/market_state/RatesType';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import PromotionOverviewService from '@app/model/promotion/service/PromotionOverviewService';
import { PromotionOverviewQueryKeys } from '@app/model/promotion/service/PromotionOverviewQueryKeys';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import PromotionsFilterBar from './PromotionsFilterBar';
import NightStrip from './NightStrip';
import CountryMatrix from './CountryMatrix';
import { formatDayYear } from './PromotionsPage.utilities';

const TOOLTIP_ID = 'promotions-tooltip';

const PromotionsPage: React.FunctionComponent = () => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { filter } = useMarketStateFilterInputTools();
	const [includeBookingFunded, setIncludeBookingFunded] = useState(false);
	const region = companyHotel.settings.main.region;
	const nights = companyHotel.settings.defaultParsing.nights?.[0] ?? 1;

	const promotionFilter = useMemo<MarketStateFilterInput>(() => ({
		...filter,
		roomTypes: [],
		provider: DataProvider.BOOKING,
		platform: DataPlatform.WEB,
		ratesType: RatesType.PUBLIC,
		region,
		guests: 2,
		nights,
		competitorTagIds: [],
		airportCode: undefined,
	}), [filter, nights, region]);

	const query = useQuery({
		queryKey: PromotionOverviewQueryKeys.detail(companyHotel.id, promotionFilter, includeBookingFunded),
		queryFn: () => PromotionOverviewService.find({
			companyHotelId: companyHotel.id,
			filterInput: promotionFilter,
			includeBookingFunded,
		}),
	});

	const meta = query.data?.meta;
	const sourceNote = meta?.latestScan != null
		? `Booking.com · web · public rates · ${nights} ${nights === 1 ? 'night' : 'nights'} · ${companyHotel.settings.main.currency}. `
			+ `Latest scan ${formatDayYear(meta.latestScan.slice(0, 10))}, ${meta.latestScan.slice(11, 16)} UTC. `
			+ 'A deal is a promotion on any available public rate of the hotel.'
		: '';

	return <>
		<PromotionsFilterBar nights={nights}
			includeBookingFunded={includeBookingFunded} onIncludeBookingFunded={setIncludeBookingFunded} />
		<main className="bg-gray-50">
			<div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
				{query.isLoading && <div className="rounded-lg bg-white px-5 py-16 shadow"><Loader /></div>}
				{query.isError &&
					<div className="rounded-lg bg-red-50 px-5 py-4 text-sm text-red-800 shadow">
						The promotions could not be loaded. Please try again.
					</div>}
				{!!query.data && query.data.rows.length === 0 &&
					<div className="rounded-lg bg-white px-5 py-16 text-center text-sm text-gray-500 shadow">
						No Booking.com web rates were read for this hotel yet, so there are no deals to show.
					</div>}
				{!!query.data && query.data.rows.length > 0 &&
					<NightStrip overview={query.data} sourceNote={sourceNote}
						includeBookingFunded={includeBookingFunded} />}
				<div className="mt-5"><CountryMatrix /></div>
			</div>
		</main>
		<Tooltip id={TOOLTIP_ID} className="!z-50 max-w-sm whitespace-pre-line" />
	</>;
};

export default PromotionsPage;
